import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';
import { isAdminUser, isOpsManager, isWarehouseUser } from './accessControl.js';
import ListFilterBar from './components/ListFilterBar.js';
import StatusBadge from './components/StatusBadge.js';

const ReadyToDispatch = ({ db, user }) => {
  const navigate = useNavigate();
  const [mocs, setMocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterType, setFilterType] = useState('All');

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  useEffect(() => {
    setLoading(true);
    const dispatchStatuses = isWarehouseUser(user)
      ? ["dc_pending", "packed_ready", "invoice_ready", "ewaybill_complete", "dc_confirmed", "qc_passed", "dispatched", "shipped", "partially_shipped", "in_transit"]
      : ["dc_pending", "packed_ready", "invoice_pending", "ewaybill_pending", "invoice_ready", "ewaybill_complete", "dc_confirmed", "qc_passed", "dispatched", "shipped", "partially_shipped", "in_transit"];

    // Ops can prepare DC from packed-ready; warehouse sees dispatch tracking from QC-ready onward.
    const q = query(
      collection(db, "dispatches"),
      where("dispatchStatus", "in", dispatchStatuses),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const mocList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMocs(mocList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching DC list: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db, user]);

  const warehouseOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.warehouse).filter(Boolean)))], [mocs]);
  const statusOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.dispatchStatus).filter(Boolean)))], [mocs]);
  const typeOptions = useMemo(() => ['All', ...Array.from(new Set(mocs.map((moc) => moc.typeOfMOC).filter(Boolean)))], [mocs]);
  const filterFields = useMemo(() => ([
    {
      key: 'searchTerm',
      type: 'search',
      label: 'Customer Search',
      placeholder: 'Search by Customer, MOC No, or Order No...',
      minWidth: '260px'
    },
    {
      key: 'filterWarehouse',
      type: 'select',
      label: 'Warehouse',
      options: warehouseOptions.map((option) => ({
        value: option,
        label: option === 'All' ? 'All Warehouses' : option
      }))
    },
    {
      key: 'filterStatus',
      type: 'select',
      label: 'Status',
      options: statusOptions.map((option) => ({
        value: option,
        label: option === 'All' ? 'All Statuses' : option
      }))
    },
    {
      key: 'filterType',
      type: 'select',
      label: 'MOC Type',
      options: typeOptions.map((option) => ({
        value: option,
        label: option === 'All' ? 'All Types' : option
      }))
    }
  ]), [warehouseOptions, statusOptions, typeOptions]);

  const filteredMocs = useMemo(() => {
    return mocs.filter(moc => {
      if (moc.dispatchStatus === 'delivered') return false;
      const matchesSearch = (
        moc.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moc.mocNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        moc.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      const matchesWarehouse = filterWarehouse === 'All' || moc.warehouse === filterWarehouse;
      const matchesStatus = filterStatus === 'All' || moc.dispatchStatus === filterStatus;
      const matchesType = filterType === 'All' || moc.typeOfMOC === filterType;
      return matchesSearch && matchesWarehouse && matchesStatus && matchesType;
    });
  }, [mocs, searchTerm, filterWarehouse, filterStatus, filterType]);

  const formatDate = (dateString) => {
    if (!dateString) return '---';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-IN', options);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'dc_pending':
        return <StatusBadge status="dc_pending" iconSize={12} />;
      case 'packed_ready':
        return <StatusBadge status="packed_ready" iconSize={12} />;
      case 'invoice_pending':
        return <StatusBadge status="invoice_pending" iconSize={12} />;
      case 'ewaybill_pending':
        return <StatusBadge status="ewaybill_pending" iconSize={12} />;
      case 'invoice_ready':
        return <StatusBadge status="invoice_ready" iconSize={12} />;
      case 'ewaybill_complete':
        return <StatusBadge status="ewaybill_complete" iconSize={12} />;
      case 'dc_confirmed':
      case 'qc_pending':
        return <div style={{ fontSize: '11px', color: '#10b981', fontWeight: 'bold' }}>INVOICE READY</div>;
      case 'qc_passed':
        return <div style={{ fontSize: '11px', color: '#10b981', fontWeight: 'bold' }}>QC PASSED</div>;
      case 'dispatched':
        return <StatusBadge status="dispatched" iconSize={12} />;
      case 'shipped':
        return <StatusBadge status="dispatched" iconSize={12} />;
      case 'partially_shipped':
        return <div style={{ fontSize: '11px', color: '#4338ca', fontWeight: 'bold' }}>PARTIALLY SHIPPED</div>;
      case 'in_transit':
        return <div style={{ fontSize: '11px', color: '#f97316', fontWeight: 'bold' }}>IN TRANSIT</div>;
      case 'delivered':
        return <StatusBadge status="delivered" iconSize={12} />;
      default:
        return <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'bold' }}>{status?.replace(/_/g, ' ').toUpperCase()}</div>;
    }
  };

  const handleRowClick = (moc) => {
    navigate(`/ops/dispatch/${moc.id}`);
  };

  const getActionConfig = (moc) => {
    if (isAdminUser(user)) {
      return {
        label: 'Preview',
        style: previewBtnStyle,
        onClick: () => navigate(`/ops/dispatch/${moc.id}`)
      };
    }

    const isOpsOrOpsHead = user?.role === 'ops' || isOpsManager(user);

    if (isOpsOrOpsHead && (moc.dispatchStatus === 'dc_pending' || moc.dispatchStatus === 'packed_ready')) {
      return {
        label: 'Edit DC',
        style: editDcBtnStyle,
        onClick: () => navigate(`/ops/dispatch/${moc.id}`, { state: { mode: 'edit' } })
      };
    }

    if (isOpsOrOpsHead && moc.dispatchStatus === 'ewaybill_pending') {
      return {
        label: 'Upload Eway Bill',
        style: editDcBtnStyle,
        onClick: () => navigate(`/ops/dispatch/${moc.id}`)
      };
    }

    return {
      label: 'Preview',
      style: previewBtnStyle,
      onClick: () => navigate(`/ops/dispatch/${moc.id}`)
    };
  };

  const getQueueDate = (moc) => {
    const sourceDate = moc.packingScheduledAt || moc.packedAt || moc.dcCreatedAt || moc.updatedAt;
    return sourceDate?.toDate ? sourceDate.toDate() : sourceDate;
  };

  const handleFilterChange = (key, value) => {
    if (key === 'searchTerm') setSearchTerm(value);
    if (key === 'filterWarehouse') setFilterWarehouse(value);
    if (key === 'filterStatus') setFilterStatus(value);
    if (key === 'filterType') setFilterType(value);
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h2 style={{ margin: 0 }}>Ready to Dispatch (DC Queue)</h2>
          <p style={{ color: '#64748b', marginTop: '5px' }}>Generate Delivery Challans and upload dispatch documents.</p>
        </div>
      </div>

      <ListFilterBar
        fields={filterFields}
        values={{ searchTerm, filterWarehouse, filterStatus, filterType }}
        onFieldChange={handleFilterChange}
      />

      {mocs.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <FileText size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#374151' }}>No material waiting for DC!</h3>
          <p style={{ color: '#64748b' }}>Items appear here once packing is complete.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Customer</th>
                <th style={thStyle}>Order No./Transfer No.</th>
                <th style={thStyle}>Challan No.</th>
                <th style={thStyle}>Warehouse</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredMocs.map((moc) => {
                const actionConfig = getActionConfig(moc);

                return (
                  <tr
                    key={moc.id}
                    style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                    onClick={() => handleRowClick(moc)}
                    className="hover-highlight"
                  >
                    <td style={tdStyle}>{formatDate(getQueueDate(moc))}</td>
                    <td style={{ ...tdStyle, textAlign: 'left' }}>
                      <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{moc.customerName}</div>
                    </td>
                    <td style={tdStyle}>{moc.typeOfMOC === 'Interbranch Transfer' ? (moc.poNumber || '---') : (moc.orderNumber || '---')}</td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 'bold', fontSize: '13px' }}>{moc.challanNo || '---'}</div>
                    </td>
                    <td style={tdStyle}>{moc.warehouse}</td>
                    <td style={tdStyle}>{getStatusBadge(moc.dispatchStatus)}</td>
                    <td style={tdStyle}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          actionConfig.onClick();
                        }}
                        style={actionConfig.style}
                      >
                        {actionConfig.label}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center' };
const previewBtnStyle = { backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '8px 12px', borderRadius: '8px', fontWeight: '700', fontSize: '12px', cursor: 'pointer' };
const editDcBtnStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer', fontSize: '12px' };

export default ReadyToDispatch;
